import { Component, OnInit, ViewChild } from '@angular/core';
import { PopupService } from '../../../shared/modal/popup.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JhiEventManager } from '../../../shared/core-utils';
import { AlertService } from '../../../shared/alert/alert.service';
import { SiteAdminContext } from '../../site-admin-context';
import { UserService } from '../../services/user.service';
import { User } from '../../../shared/user/model/user.model';
import { EMAIL_LOCAL_PART_REGEX } from '../../validators/email-validator.component';
import { UserRole } from '../../../shared/user/model/user-role.model';
import { Crop } from '../../../shared/model/crop.model';
import { CropService } from '../../services/crop.service';
import { UserRoleDialogComponent } from './users-role-dialog.component';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { NavbarMessageEvent } from '../../../shared/model/navbar-message.event';
import { MailService } from '../../services/mail.service';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'jhi-user-edit-dialog',
    templateUrl: 'user-edit-dialog.component.html'
})

export class UserEditDialogComponent implements OnInit {

    EMAIL_LOCAL_PART_REGEX = EMAIL_LOCAL_PART_REGEX;

    model: User;
    crops: Crop[];

    cropSelected: any;

    sendingEmail: boolean;
    isEditing: boolean;
    sendMail: boolean;

    userSaved: boolean;

    @ViewChild('cropsDropdown') cropsDropdown: NgSelectComponent;

    constructor(public activeModal: NgbActiveModal,
                private eventManager: JhiEventManager,
                private userService: UserService,
                private cropService: CropService,
                private alertService: AlertService,
                private mailService: MailService,
                private modalService: NgbModal,
                private modal: NgbActiveModal,
                public translateService: TranslateService,
                public context: SiteAdminContext) {

        this.sendingEmail = false;
        this.userSaved = false;
        this.crops = [];
    }

    async ngOnInit() {
        this.model = this.context.user;
        this.cropSelected = this.model.crops.map((crop) => crop.cropName);
        this.isEditing = !!this.model.userUUID;
        this.crops = await this.cropService.getAll().toPromise();

    }

    onChangeCrop(crops: string[]) {
        if (!crops) {
            this.model.crops = [];
            return;
        }
        this.model.crops = crops.map((crop) => {
            return {
                cropName: crop
            };
        });
    }

    onSelectAllCrops($event: any) {
        if ($event.currentTarget.checked) {
            this.model.crops = Object.assign([], this.crops);
            this.cropSelected = this.crops.map((crop) => crop.cropName);
            // Make sure the selected crops reflect in the UI.
            this.cropsDropdown.writeValue(this.crops.map((crop) => crop.cropName));
            this.cropsDropdown.detectChanges();
        } else {
            this.model.crops = [];
            this.cropSelected = [];
        }
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'onUserViewChanged' });
        this.activeModal.close();
    }

    addUser(form) {
        if (this.isFormValid(form)) {
            this.userSaved = true;
            this.userService.save(this.trimAll(this.model)).subscribe((rest) => {
                this.sendUserPasswordEmail(rest, 'site-admin.user.modal.create.success');
            }, (error) => {
                this.userSaved = false;
            });
        }
    }

    updateUser(form) {
        if (this.isFormValid(form)) {
            this.userService
                .update(this.trimAll(this.model))
                .subscribe(
                    (resp) => {
                        this.userSaved = true;
                        this.sendEmailToResetPassword('site-admin.user.modal.edit.success');
                        const message: NavbarMessageEvent = { userProfileChanged: true };
                        window.parent.postMessage(message, '*');
                    });
        }
    }

    AssignRole() {
        this.modal.close();
        this.modalService.open(UserRoleDialogComponent as Component, { size: 'lg', backdrop: 'static' });
    }

    private sendUserPasswordEmail(respSaving: any, msg: any) {
        this.model = respSaving;
        this.sendingEmail = true;
        this.userService
            .sendUserPasswordEmail(this.model)
            .subscribe(
                (resp) => {
                    setTimeout(() => {
                        this.alertService.success(msg);
                        this.sendingEmail = false;
                        this.userSaved = false;
                        this.sendMail = !this.isEditing;
                        this.notifyChanges();
                    }, 1000);
                }, (error) => {
                    setTimeout(() => {
                        this.alertService.success(msg);
                        this.sendingEmail = false;
                        this.userSaved = false;
                        this.sendMail = !this.isEditing;
                    }, 2000);
                });

    }

    private sendEmailToResetPassword(msg: any) {
        if (this.sendMail) {
            this.sendingEmail = true;
            this.mailService
                .send(this.model)
                .subscribe(
                    (resp) => {
                        setTimeout(() => {
                            this.alertService.success(msg);
                            this.sendingEmail = false;
                            this.userSaved = false;
                            this.sendMail = !this.isEditing;
                            this.notifyChanges();
                        }, 1000);
                    }, (error) => {
                        setTimeout(() => {
                            this.alertService.success(msg);
                            this.sendingEmail = false;
                            this.userSaved = false;
                            this.sendMail = !this.isEditing;
                        }, 2000);
                    });
        } else {
            setTimeout(() => {
                this.alertService.success(msg);
                this.userSaved = false;
                this.sendMail = !this.isEditing;
                this.notifyChanges();
            }, 1000);
        }
    }

    private trimAll(model: User) {
        model.firstName = model.firstName.trim();
        model.lastName = model.lastName.trim();
        model.email = model.email.trim();
        model.username = model.username.trim();
        return model;
    }

    isFormValid(form) {
        return form.valid && this.model.crops && this.model.crops.length;
    }

    showDeleteUserRoleConfirmPopUp(userRole: UserRole) {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('site-admin.user.confirm.message.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('site-admin.user.modal.delete-role-assigned-message');
        confirmModalRef.result.then(() => {
            this.deleteUserRole(userRole);
        }, () => confirmModalRef.dismiss());
    }

    deleteUserRole(userRole: UserRole) {
        const idx = this.model.userRoles.indexOf(userRole);
        this.model.userRoles.splice(idx, 1);
    }
}

@Component({
    selector: 'jhi-user-edit-popup',
    template: ''
})
export class UserEditPopupComponent implements OnInit {

    constructor(private popupService: PopupService) {
    }

    ngOnInit(): void {
        this.popupService.open(UserEditDialogComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
    }
}
